/*
 * Copyright The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Document,
  isMap,
  Pair,
  parse,
  parseDocument,
  Scalar,
  YAMLSeq,
  stringify,
} from 'yaml';
import { JsonObject } from '@backstage/types';
import { ExtensionsPluginInstallStatus } from '@red-hat-developer-hub/backstage-plugin-extensions-common';
import { TranslationFunction } from '@backstage/core-plugin-api/alpha';
import { extensionsTranslationRef } from './alpha/translations';
import { PluginPermissions, Permission } from './types';

export enum ExtensionsStatus {
  INSTALLATION_DISABLED_IN_PRODUCTION = 'INSTALLATION_DISABLED_IN_PRODUCTION',
  INSTALLATION_DISABLED = 'INSTALLATION_DISABLED',
  FILE_CONFIG_VALUE_MISSING = 'FILE_CONFIG_VALUE_MISSING',
  FILE_NOT_EXISTS = 'FILE_NOT_EXISTS',
  INVALID_CONFIG = 'INVALID_CONFIG',
  UNKNOWN = 'UNKNOWN',
}

export const DYNAMIC_PLUGIN_CONFIG_YAML = `plugins:
  - package: ./dynamic-plugins/dist/red-hat-developer-hub-backstage-plugin-bulk-import-backend-dynamic
    disabled: false
  - package: ./dynamic-plugins/dist/red-hat-developer-hub-backstage-plugin-bulk-import
    disabled: true`;

export const EXTENSIONS_CONFIG_YAML = `extensions:
  installation:
    enabled: true
    saveToSingleFile:
      file: /<path-to>/dynamic-plugins.yaml`;

const getExtensionsConfigLine = (str: string) =>
  EXTENSIONS_CONFIG_YAML.split('\n').findIndex(line => line.includes(str));

const generateFileConfigLineNumbers = () => {
  const line = getExtensionsConfigLine(`saveToSingleFile:`) + 1;
  return Array.from(Array(line).keys(), i => i + line);
};

const generateFilePathLineNumbers = () => {
  const line = getExtensionsConfigLine(`file:`) + 1;
  return Array.from(Array(line).keys(), i => i + line);
};

export const generateExtensionsEnableLineNumbers = () => {
  const line = getExtensionsConfigLine(`extensions:`);
  return Array.from(Array(line + 3).keys(), i => i + (line + 1));
};

export const getExampleAsMarkdown = (content: string | JsonObject) => {
  if (!content) {
    return '';
  }
  if (typeof content === 'string') {
    return `\`\`\`yaml\n${content}\n\`\`\``;
  }
  if (Object.entries(content).length === 0) {
    return '';
  }
  const yamlString = stringify(content);
  return `\`\`\`yaml\n${yamlString}\n\`\`\``;
};

export const applyContent = (
  editorContent: string,
  packageName: string,
  otherPackageNames: { [key: string]: string },
  newContent: string | JsonObject,
) => {
  if (!editorContent || !editorContent.trim()) {
    const packagePath = otherPackageNames[packageName];
    if (!packagePath) {
      return null;
    }

    const minimalYaml = {
      plugins: [
        {
          package: packagePath,
          disabled: false,
          pluginConfig:
            typeof newContent === 'string' ? parse(newContent) : newContent,
        },
      ],
    };
    return stringify(minimalYaml);
  }
  const content = parseDocument(editorContent);
  const plugins = content.get('plugins');

  if (plugins instanceof YAMLSeq && Array.isArray(plugins?.items)) {
    let foundPackage = false;
    (plugins?.items || []).forEach(plugin => {
      if (isMap(plugin)) {
        const pluginPackage = plugin.items?.find(
          (i: Pair<unknown, unknown>) => {
            const pair = i as Pair<Scalar, Scalar>;
            return (
              pair.key.value === 'package' &&
              pair.value?.value === otherPackageNames[`${packageName}`]
            );
          },
        );
        if (pluginPackage) {
          foundPackage = true;
          if (typeof newContent === 'string') {
            plugin.set('pluginConfig', parseDocument(newContent));
          } else {
            plugin.set('pluginConfig', newContent);
          }
        }
      }
    });

    if (!foundPackage) {
      const packagePath = otherPackageNames[packageName];
      if (packagePath) {
        const newPlugin: {
          package: string;
          disabled: boolean;
          pluginConfig?: Document | JsonObject;
        } = {
          package: packagePath,
          disabled: false,
        };
        if (typeof newContent === 'string') {
          newPlugin.pluginConfig = parseDocument(newContent);
        } else {
          newPlugin.pluginConfig = newContent;
        }
        plugins.add(newPlugin);
      }
    }
  } else {
    const packagePath = otherPackageNames[packageName];
    if (packagePath) {
      const newPlugin: {
        package: string;
        disabled: boolean;
        pluginConfig?: Document | JsonObject;
      } = {
        package: packagePath,
        disabled: false,
      };
      if (typeof newContent === 'string') {
        newPlugin.pluginConfig = parseDocument(newContent);
      } else {
        newPlugin.pluginConfig = newContent;
      }
      content.set('plugins', [newPlugin]);
    }
  }
  return content.toString();
};

export function getPluginConfigResponseError(data: unknown) {
  if (data && typeof data === 'object' && 'error' in data) {
    return (data as { error?: { message?: string; reason?: ExtensionsStatus } })
      .error;
  }
  return undefined;
}

export function apiErrorMessage(value: unknown) {
  if (value && typeof value === 'object' && 'error' in value) {
    const e = (value as { error?: { message?: string } }).error;
    return e?.message;
  }
  return undefined;
}

export const getErrorMessage = (
  reason: ExtensionsStatus,
  message: string,
  t: TranslationFunction<typeof extensionsTranslationRef.T>,
) => {
  if (reason === ExtensionsStatus.FILE_CONFIG_VALUE_MISSING) {
    return {
      title: t('errors.missingConfigFile'),
      message: t('errors.missingConfigMessage' as any, { message }),
      highlightedLineNumbers: generateFileConfigLineNumbers(),
    };
  }
  if (reason === ExtensionsStatus.INVALID_CONFIG) {
    return {
      title: t('errors.invalidConfigFile'),
      message: t('errors.invalidConfigMessage' as any, { message }),
    };
  }

  if (reason === ExtensionsStatus.FILE_NOT_EXISTS) {
    return {
      title: t('errors.fileNotExists'),
      message: t('errors.fileNotExistsMessage' as any, { message }),
      highlightedLineNumbers: generateFilePathLineNumbers(),
    };
  }
  if (reason === ExtensionsStatus.UNKNOWN) {
    return {
      title: t('errors.unknownError'),
      message,
    };
  }
  return { title: '', message: '' };
};

export const getPluginActionTooltipMessage = (
  isProductionEnvironment: boolean,
  permissions: PluginPermissions | null,
  t: TranslationFunction<typeof extensionsTranslationRef.T>,
  extensionsDisabled?: boolean,
  missingDynamicArtifact?: boolean,
  isPlugin: boolean = true,
): string | null => {
  if (isProductionEnvironment) {
    return t('tooltips.productionDisabled');
  }
  if (extensionsDisabled) {
    return t('tooltips.extensionsDisabled');
  }
  if (
    permissions &&
    permissions.read !== Permission.ALLOW &&
    permissions.write !== Permission.ALLOW
  ) {
    return t('tooltips.noPermissions');
  }
  if (missingDynamicArtifact) {
    return t('tooltips.missingDynamicArtifact' as any, {
      type: isPlugin ? 'plugin' : 'package',
    });
  }

  return null;
};

export const isPluginInstalled = (
  pluginInstallStatus: ExtensionsPluginInstallStatus | undefined,
) => {
  if (!pluginInstallStatus) {
    return false;
  }
  return (
    pluginInstallStatus === ExtensionsPluginInstallStatus.Installed ||
    pluginInstallStatus === ExtensionsPluginInstallStatus.UpdateAvailable ||
    pluginInstallStatus === ExtensionsPluginInstallStatus.PartiallyInstalled ||
    pluginInstallStatus === ExtensionsPluginInstallStatus.Disabled
  );
};

export interface CategoryTagDisplayOptions {
  maxLength?: number;
}

export const getCategoryTagDisplayInfo = (
  categoryName: string,
  options: CategoryTagDisplayOptions = {},
) => {
  const { maxLength = 25 } = options;

  const shouldTruncate = categoryName.length > maxLength;
  const displayName = shouldTruncate
    ? `${categoryName.substring(0, maxLength)}...`
    : categoryName;

  return {
    displayName,
    tooltipTitle: shouldTruncate ? categoryName : '',
    shouldShowTooltip: shouldTruncate,
  };
};
